import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Badge, Button, Card, EmptyState, Page, PageHeader, PageScroll, StatusHint } from '@indipia/ui-core';
import { siteStatusBadge, siteStatusLabel } from '../utils/siteStatus';
import { usePhotoNavStore } from '../store/photoNavStore';
import { PhotoCard } from '../components/PhotoCard';
import { photoApi } from '../api/photoApi';
import type { PanelSiteListItem, SitePhotoItem } from '../api/photoApi';
import type { ShareSiteInfo } from '../types/photo';
import { SitePanel } from './SitePanel';
import { useRightSlot } from '../hooks/useRightSlot';
import { ExternalLinkSend, ShareLinkPopup, shareViaKakao, type Recipient } from '@indipia/shared-features';
import { apiClient, API_PRX } from '@indipia/shared';
import { fetchPhotoShareLink, apiErrorInfo } from '../api/shareAdapter';
import { usePhotoSharePreview, pickGeneralRecipient } from '../hooks/usePhotoSharePreview';
import { SharePreviewPanel } from '../components/SharePreviewPanel';

const BASE = '/agents/photo-organizer';

// 알림톡 발송 가능 기간(공사 종료일 + 7일) 경과 — 서버 422 SHARE_SEND_WINDOW_CLOSED 안내
const WINDOW_CLOSED_MESSAGE = '공사 종료일 + 7일이 지나 알림톡을 보낼 수 없어요. 링크를 복사해 직접 보내 주세요.';

/** 서버가 준 만료 시각('YYYY-MM-DDT…')을 M/D 로 표기 — 날짜 계산 없이 문자열만 나눈다. */
function toMonthDay(expiresAt: string | null | undefined): string {
  if (!expiresAt) return '';
  const [, m, d] = expiresAt.slice(0, 10).split('-');
  return m && d ? `${Number(m)}/${Number(d)}` : '';
}

function CardHeader({ iconBg, iconColor, iconNode, title, badge, right }: {
  iconBg: string; iconColor: string; iconNode: React.ReactNode;
  title: React.ReactNode; badge?: React.ReactNode; right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 px-5 py-3.5">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md" style={{ background: iconBg }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{iconNode}</svg>
      </div>
      <h3 className="text-heading-sm font-bold text-text">{title}</h3>
      {badge}
      <div className="flex-1" />
      {right}
    </div>
  );
}

export function ShareSendMain() {
  useEffect(() => {
    const main = document.querySelector('main');
    if (main) main.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const navigate          = useNavigate();
  const [compName, setCompName] = useState('');

  useEffect(() => {
    apiClient.get(`${API_PRX.MEMBER}/users/me`)
      .then(res => { const name = res.data?.data?.compName; if (name) setCompName(name); })
      .catch(() => {});
  }, []);

  // 현장 상세에서 스토어로 넘긴 현장·사진 — 진입 시 한 번만 읽는 초기값이다.
  const storeSite    = usePhotoNavStore(s => s.shareSite);
  const storePhotos  = usePhotoNavStore(s => s.sharePhotos);
  const storeScope   = usePhotoNavStore(s => s.shareScope);
  const setShareSend = usePhotoNavStore(s => s.setShareSend);

  // 공유 대상은 항상 한 현장 — 우측 현장 패널에서 고르고, 현장 상세에서 들어오면 그 현장으로 시작한다.
  const [selectedSite, setSelectedSite] = useState<ShareSiteInfo | null>(storeSite);
  // 공유할 사진 선택 — 기본은 현장의 모든 사진이 선택된 상태(allSelected). 아직 불러오지 않은 페이지의 사진까지 포함해야 해서
  // "전부 선택 − 제외한 사진(excludedIds)"으로 표현하고, 직접 고르는 방식(allSelected=false)은 selectedIds만 쓴다.
  // 공유는 항상 발송 시점에 체크된 사진 목록으로 고정된다 — 공유 이후 올라오는 사진은 고객에게 열리지 않는다.
  const startsWithPhotos = !!storeSite && storeScope === 'photos';
  const [allSelected, setAllSelected] = useState(!startsWithPhotos);
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(startsWithPhotos ? storePhotos.map(p => p.id) : []),
  );
  // 스토어 값은 1회성 — 새로고침·메뉴 재진입 때 이전 공유 대상이 되살아나지 않게 비운다.
  useEffect(() => {
    setShareSend([], null, 'site-detail', 'site');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bundleIdRef = useRef<string | null>(null);

  // [공유하기] 팝업 — 고객 고정 링크 복사·카카오톡 공유. 현장이 바뀌면 닫는다.
  const [sharePopupOpen, setSharePopupOpen] = useState(false);

  const siteId = selectedSite?.siteId ?? null;
  const siteIdRef = useRef<number | null>(siteId);
  siteIdRef.current = siteId;
  useEffect(() => { setSharePopupOpen(false); }, [siteId]);

  // 선택 현장의 사진 목록 — 현장이 바뀔 때마다 새로 불러온다(다른 현장 사진은 섞이지 않는다).
  const PHOTO_PAGE_SIZE = 60;
  const [sitePhotos, setSitePhotos] = useState<SitePhotoItem[]>([]);
  const [photoTotal, setPhotoTotal] = useState(0);
  const [photoHasNext, setPhotoHasNext] = useState(false);
  const [photosLoading, setPhotosLoading] = useState(false);
  useEffect(() => {
    setSitePhotos([]);
    setPhotoTotal(0);
    setPhotoHasNext(false);
    if (siteId == null) return;
    let cancelled = false;
    setPhotosLoading(true);
    photoApi.getSitePhotos(siteId, PHOTO_PAGE_SIZE, 0)
      .then(res => {
        if (cancelled) return;
        const d = res.data.data;
        setSitePhotos(d.photos);
        setPhotoTotal(d.totalCount);
        setPhotoHasNext(d.hasNext);
      })
      .catch(() => { if (!cancelled) toast.error('사진 목록을 불러오지 못했습니다.'); })
      .finally(() => { if (!cancelled) setPhotosLoading(false); });
    return () => { cancelled = true; };
  }, [siteId]);

  function loadMorePhotos() {
    if (siteId == null || photosLoading) return;
    setPhotosLoading(true);
    photoApi.getSitePhotos(siteId, PHOTO_PAGE_SIZE, sitePhotos.length)
      .then(res => {
        if (siteIdRef.current !== siteId) return;
        const d = res.data.data;
        setSitePhotos(prev => [...prev, ...d.photos]);
        setPhotoHasNext(d.hasNext);
      })
      .catch(() => toast.error('사진 목록을 불러오지 못했습니다.'))
      .finally(() => setPhotosLoading(false));
  }

  // 우측 현장 패널에서 현장을 바꾸면 사진 선택은 초기화하고 전체 선택 상태로 되돌린다(현장마다 사진이 달라서).
  async function handleSelectSite(item: PanelSiteListItem) {
    if (item.siteId === siteIdRef.current) return;
    try {
      const res = await photoApi.getSiteDetail(item.siteId);
      const d = res.data.data;
      setSelectedSite({
        siteId: d.siteId, siteName: d.siteName, siteAddress: d.address,
        customerName: d.customerName, customerMobile: d.customerMobile, siteStatus: d.siteStatus,
      });
      setAllSelected(true);
      setExcludedIds(new Set());
      setSelectedIds(new Set());
    } catch {
      toast.error('현장 정보를 불러오지 못했습니다.');
    }
  }
  useRightSlot(<SitePanel currentSiteId={siteId} onSelectSite={handleSelectSite} />, [siteId]);

  const selectedCount = allSelected ? Math.max(photoTotal - excludedIds.size, 0) : selectedIds.size;
  const isChecked = (photoId: number) => (allSelected ? !excludedIds.has(photoId) : selectedIds.has(photoId));

  function toggleSelected(photoId: number) {
    const flip = (prev: Set<number>) => {
      const next = new Set(prev);
      if (next.has(photoId)) next.delete(photoId); else next.add(photoId);
      return next;
    };
    if (allSelected) setExcludedIds(flip); else setSelectedIds(flip);
  }

  // 현장의 모든 사진(불러오지 않은 페이지 포함)을 선택 / 전부 해제
  function selectAllPhotos() {
    setAllSelected(true);
    setExcludedIds(new Set());
    setSelectedIds(new Set());
  }
  function clearAllPhotos() {
    setAllSelected(false);
    setExcludedIds(new Set());
    setSelectedIds(new Set());
  }

  // 발송 직전에 체크된 사진 ID 전체를 확정한다. 전체 선택 상태면 아직 불러오지 않은 페이지까지 모두 가져온 뒤 제외분을 뺀다.
  async function collectSelectedPhotoIds(): Promise<number[]> {
    if (!allSelected) return Array.from(selectedIds);
    const ids = sitePhotos.map(p => p.photoId);
    let hasNext = photoHasNext;
    while (hasNext && siteId != null) {
      const res = await photoApi.getSitePhotos(siteId, PHOTO_PAGE_SIZE, ids.length);
      const d = res.data.data;
      ids.push(...d.photos.map(p => p.photoId));
      hasNext = d.hasNext && d.photos.length > 0;
    }
    return ids.filter(id => !excludedIds.has(id));
  }

  const contextKey = `site-${siteId ?? 'none'}`;

  const initialExpiresAt = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return d.toISOString().slice(0, 10);
  }, []);

  const [expiresAt, setExpiresAt] = useState(initialExpiresAt);

  // ── 알림톡 미리보기 — 서버 렌더(POST /photo/shares/preview). 문구·유효기간은 서버 값만 쓰고 화면에서 조립·계산하지 않는다. ──
  // 수신자 목록·제목·내용은 공용 발송 폼(ExternalLinkSend)이 들고 있어 변경 알림(onRecipientsChange·onMessageChange)으로 받는다.
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [msgTitle, setMsgTitle] = useState('');
  const [msgContent, setMsgContent] = useState('');
  const [sendWindowClosed, setSendWindowClosed] = useState(false);
  // 현장이 바뀌면 ExternalLinkSend 가 새로 마운트되어 제목·내용이 비워진다 — 수신자는 마운트 때 다시 알려 준다.
  useEffect(() => { setMsgTitle(''); setMsgContent(''); setSendWindowClosed(false); }, [siteId]);

  function handleMessageChange(v: { title: string; content: string }) {
    setMsgTitle(v.title);
    setMsgContent(v.content);
  }

  const hasPhotos = selectedCount > 0;
  // 미리보기는 사진 번호를 보내지 않고 선택 장수(photoCount)만 보낸다.
  // 전체 선택 = 사진 목록 응답 totalCount − 제외 장수, 직접 선택 = 고른 장수(selectedCount 가 이미 이 값).
  // 전체 장수를 아직 못 받았으면(목록 로딩 중·실패) 추정하지 않고 생략 — 서버가 현장 사진 수를 센다.
  const totalKnown = photoTotal > 0 || !allSelected;
  const previewPhotoCount = totalKnown ? selectedCount : undefined;

  const customerName = recipients.find(r => r.recipientType === 'CUSTOMER')?.name ?? selectedSite?.customerName ?? '';
  const customerRecipient = useMemo(
    () => (customerName ? { name: customerName, recipientType: 'CUSTOMER' } : null),
    [customerName],
  );
  const generalRecipient = useMemo(() => pickGeneralRecipient(recipients), [recipients]);

  const previewBase = { siteId: siteId ?? undefined, title: msgTitle, content: msgContent, expiresAt, photoCount: previewPhotoCount, hasPhotos };
  const customerPreview = usePhotoSharePreview({ ...previewBase, group: 'CUSTOMER', recipient: customerRecipient });
  const generalPreview = usePhotoSharePreview({ ...previewBase, group: 'GENERAL', recipient: generalRecipient });

  // 고객 링크 기간(공사 종료일 + 7일)은 좌측(고객) 영역 유효기간 카드에 읽기 전용으로 안내 — 날짜는 미리보기 응답 expiresAt 만 쓴다.
  const customerMd = toMonthDay(customerPreview.data?.expiresAt);

  return (
    // D-07 §21.4 fixed — 인디포토 공통 레이아웃이 높이를 묶으므로 doc 을 쓸 수 없다(나의 사진첩 현장 목록과 같은 틀).
    // 스크롤·폭·패딩은 PageScroll 이 갖는다(§21.3 xl = 1440). 스크롤 맨 위로(§15.55)도 PageScroll 이 함께 단다.
    <Page layout="fixed" style={{ background: 'var(--shell-bg)' }}>
    <PageScroll width="xl">
    <div className="flex flex-col gap-4">
        <PageHeader title="정리한 사진 공유" subtitle="현장 사진을 골라 고객 · 반장님 · 거래처에게 알림톡이나 링크로 보내요" />

        {/* ── 선택한 현장 ── */}
        {selectedSite && <Card>
          <div style={{ margin: -16 }}>
          <CardHeader
            iconBg="var(--color-errorSubtle)" iconColor="var(--color-error)"
            iconNode={<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>}
            title="선택한 현장"
          />
          <div style={{ padding: '0 20px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', background: 'var(--shell-bg)', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--shell-border)' }}>
              <div style={{ width: 38, height: 38, borderRadius: 'var(--radius-md)', background: 'var(--shell-skeleton)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                  <span className="text-body-md font-bold text-text">{selectedSite?.siteName}</span>
                  {/* 상태 = Badge(D-09 §15.5) — 색은 인디포토 단일 기준(siteStatusBadge) */}
                  {selectedSite?.siteStatus && (
                    <Badge status={siteStatusBadge(selectedSite.siteStatus)}>{siteStatusLabel(selectedSite.siteStatus)}</Badge>
                  )}
                </div>
                {selectedSite?.siteAddress && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--shell-hint)', marginBottom: 2 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                    {selectedSite.siteAddress}
                  </div>
                )}
                {selectedSite?.customerName && (
                  <div style={{ fontSize: 12, color: 'var(--shell-hint)' }}>고객명 : {selectedSite.customerName}</div>
                )}
              </div>
            </div>
          </div>
          </div>
        </Card>}

        {/* ── 공유할 사진 — 기본은 현장의 모든 사진이 체크된 상태 ── */}
        {selectedSite && <Card>
          <div style={{ margin: -16 }}>
          <CardHeader
            iconBg="var(--color-errorSubtle)" iconColor="var(--color-error)"
            iconNode={<><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>}
            title="공유할 사진"
            badge={<Badge status="info">{`${selectedCount} / ${photoTotal}장 선택`}</Badge>}
            right={
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={selectAllPhotos} disabled={allSelected && excludedIds.size === 0}>전체 선택</Button>
                <Button variant="secondary" size="sm" onClick={clearAllPhotos} disabled={selectedCount === 0}>전체 해제</Button>
              </div>
            }
          />
          <div style={{ padding: '0 20px 20px' }}>
            <p style={{ margin: '0 0 12px', fontSize: 12.5, color: selectedCount === 0 ? 'var(--color-error)' : 'var(--shell-hint)' }}>
              {selectedCount === 0
                ? '공유할 사진을 1장 이상 선택하세요.'
                : '체크한 사진만 공유되며, 공유 이후 올라오는 사진은 고객에게 열리지 않습니다.'}
            </p>
            {sitePhotos.length === 0 && !photosLoading ? (
              <EmptyState title="공유할 사진이 없습니다" description="이 현장에 등록된 사진이 없습니다." />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                {sitePhotos.map(p => (
                  <PhotoCard
                    key={p.photoId}
                    photoId={p.photoId}
                    thumbUrl={p.thumbUrl}
                    takenAt={p.takenAt}
                    registeredAt={p.registeredAt}
                    showTags={false}
                    showCheckbox
                    checked={isChecked(p.photoId)}
                    onToggle={() => toggleSelected(p.photoId)}
                  />
                ))}
              </div>
            )}
            {photoHasNext && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                <Button variant="secondary" size="sm" onClick={loadMorePhotos} disabled={photosLoading}>{photosLoading ? '불러오는 중…' : '사진 더 보기'}</Button>
              </div>
            )}
          </div>
          </div>
        </Card>}

        {/* ── 현장 미선택 ── */}
        {!selectedSite && <Card>
          <EmptyState title="공유할 현장을 선택하세요" description="우측 현장 목록에서 현장을 선택하면 사진 선택과 수신자 지정 화면이 열립니다." />
        </Card>}

        {/* ── URL 공유 (ExternalLinkSend) + 서버 렌더 알림톡 미리보기 ── */}
        {selectedSite && <>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
        {sendWindowClosed && (
          <div style={{ marginBottom: 12 }}>
            <StatusHint tone="warning">{WINDOW_CLOSED_MESSAGE}</StatusHint>
          </div>
        )}
        <ExternalLinkSend
          key={contextKey}
          sourceService="sv-photo"
          sourceAgent="A01"
          resourceType="PHOTO_GROUP"
          shareType="PHOTO_ALBUM_SHARE"
          shareScope="READ"
          sendOverride={async (p) => {
            try {
              setSendWindowClosed(false);
              return await photoApi.sendPhotoShare(p);
            } catch (err) {
              if (apiErrorInfo(err).code === 'SHARE_SEND_WINDOW_CLOSED') {
                setSendWindowClosed(true);
                // 공용 발송 훅은 응답 message 를 토스트로 보여 준다 — 같은 문구 스타일로 바꿔 전달한다.
                throw Object.assign(new Error(WINDOW_CLOSED_MESSAGE), { response: { data: { message: WINDOW_CLOSED_MESSAGE } } });
              }
              throw err;
            }
          }}
          customerExpiryNotice={<StatusHint tone="info">고객: 공사 종료일 + 7일{customerMd ? `(${customerMd})` : ''}까지</StatusHint>}
          initialExpiresAt={initialExpiresAt}
          onExpiresAtChange={setExpiresAt}
          onMessageChange={handleMessageChange}
          onRecipientsChange={setRecipients}
          initialRecipients={
            selectedSite?.customerName && selectedSite?.customerMobile
              ? [{ name: selectedSite.customerName, mobile: selectedSite.customerMobile, recipientType: 'CUSTOMER' as const }]
              : undefined
          }
          // 회사명·수신자명·유효기간·링크는 서버가 채운다 — 화면은 사용자가 입력한 제목·내용만 보낸다(공용 폼이 병합).
          resolveButtons={() => [
            { type: 'WL', name: '공유 사진첩 보기', resourceRef: bundleIdRef.current ?? '' },
          ]}
          sendLabel="알림톡 보내기"
          extraActions={
            <Button variant="secondary" type="button" onClick={() => setSharePopupOpen(true)} className="flex-none">
              공유하기
            </Button>
          }
          resolveResourceRef={async () => {
            // 현장 필수(번들은 항상 한 현장). 사진 선택 모드에서 0장이면 발송하지 않는다.
            if (!selectedSite) throw new Error('공유할 현장이 선택되지 않았습니다.');
            // 번들은 항상 발송 시점에 체크된 사진 목록으로 고정한다(공유 이후 올라오는 사진은 고객에게 열리지 않는다).
            const photoIds = await collectSelectedPhotoIds();
            if (photoIds.length === 0) throw new Error('공유할 사진이 선택되지 않았습니다.');
            const res = await photoApi.createShareBundle(
              selectedSite.siteId,
              selectedSite.siteName,
              photoIds.map(photoId => ({ photoId, siteId: selectedSite.siteId, siteName: selectedSite.siteName })),
            );
            bundleIdRef.current = res.data.data.bundleId;
            return res.data.data.bundleId;
          }}
          // 이력은 서버가 발송 접수 때 기록한다 — 화면은 기록 호출 없이 이력 화면으로 이동만 한다.
          onSent={(result) => { if (result.sentCount > 0) navigate(`${BASE}/share-history`); }}
        />
        </div>
        <div style={{ width: 300, flexShrink: 0, position: 'sticky', top: 0 }}>
          <Card>
            <div style={{ margin: -16 }}>
              <CardHeader
                iconBg="var(--color-errorSubtle)" iconColor="var(--color-error)"
                iconNode={<path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />}
                title="카카오 알림톡 미리보기"
              />
              <div style={{ padding: '0 var(--spacing-05) var(--spacing-05)' }}>
                <SharePreviewPanel
                  customer={customerPreview}
                  general={generalPreview}
                  hasCustomer={customerRecipient != null}
                  generalRecipientName={generalRecipient?.name ?? null}
                  generalInputReady={msgTitle.trim() !== '' && msgContent.trim() !== '' && expiresAt !== ''}
                  hasPhotos={hasPhotos}
                />
              </div>
            </div>
          </Card>
        </div>
        </div>
        <ShareLinkPopup
          open={sharePopupOpen}
          onClose={() => setSharePopupOpen(false)}
          siteId={selectedSite.siteId}
          fetchLink={() => fetchPhotoShareLink(selectedSite.siteId)}
          onKakaoShare={(url) => shareViaKakao({
            url,
            title: `${selectedSite.siteName} 현장 사진첩`,
            description: compName ? `${compName}에서 공유한 현장 사진이에요` : '공유된 현장 사진이에요',
            imageUrl: `${window.location.origin}/brand/indipia-logo.png`,
          })}
        />
        </>}

    </div>
    </PageScroll>
    </Page>
  );
}
